import React, { useEffect, useMemo } from "react";
import * as THREE from "three";

import { Environment } from "@react-three/drei";
import { useThree } from "@react-three/fiber";

import { Theme } from "./types";

/**
 * Scene background and fog controller component
 * Handles scene-level properties that need to be set on the Three.js scene
 */
function SceneController({
  theme,
  themeIntensity,
}: {
  theme: Theme;
  themeIntensity: number;
}) {
  const { scene } = useThree();

  // Theme colors based on mode and intensity
  const themeSettings = useMemo(() => {
    let colors;
    if (theme === "dark") {
      colors = {
        background: new THREE.Color("#2a2a2a"), // Dark grey
        fog: new THREE.Color("#3a3a3a"), // Lighter dark fog
      };
    } else {
      colors = {
        background: new THREE.Color("#f5f5f5"), // Almost white
        fog: new THREE.Color("#f5f5f5"), // White fog
      };
    }

    // Apply intensity scaling (0.5 = -50%, 1.5 = +50%)
    // Invert value for light theme (2 - value: 1.5 becomes 0.5, 0.5 becomes 1.5, 1.0 stays 1.0)
    const adjustedThemeIntensity =
      theme === "dark" ? 2 - themeIntensity : themeIntensity;
    const adjustedBackground = colors.background
      .clone()
      .multiplyScalar(adjustedThemeIntensity);
    const adjustedFog = colors.fog
      .clone()
      .multiplyScalar(adjustedThemeIntensity);

    return {
      background: adjustedBackground,
      fog: adjustedFog,
    };
  }, [theme, themeIntensity]);

  // Set scene background and fog
  useEffect(() => {
    scene.background = themeSettings.background;
    scene.fog = new THREE.Fog(themeSettings.fog, 10, 50);

    return () => {
      scene.background = null;
      scene.fog = null;
    };
  }, [scene, themeSettings]);

  return null;
}

interface StudioEnvironmentProps {
  /** Theme determining the ambiance */
  theme: Theme;
  /** Environment lighting intensity */
  environmentIntensity: number;
  /** Theme intensity affecting background and fog colors */
  themeIntensity: number;
}

/**
 * Professional studio environment with infinite floor and background
 *
 * Creates a seamless studio setup with:
 * - Infinite ground plane with proper shadow receiving
 * - Gradient background matching the theme
 * - Environment mapping for realistic reflections
 * - Proper fog effects for depth and atmosphere
 *
 * @param props - Environment configuration props
 * @returns JSX elements for the complete studio environment
 */
export function StudioEnvironment({
  theme: mode,
  environmentIntensity,
  themeIntensity,
}: StudioEnvironmentProps) {
  // Theme colors based on mode
  const themeColors = useMemo(() => {
    if (mode === "dark") {
      return {
        ground: new THREE.Color("#1a1a1a"), // Dark grey
        background: new THREE.Color("#0a0a0a"), // Almost black
        environmentBg: 0.05,
      };
    } else {
      return {
        ground: new THREE.Color("#e0e0e0"), // Light grey
        background: new THREE.Color("#f5f5f5"), // Almost white
        environmentBg: 0.8,
      };
    }
  }, [mode]);

  return (
    <>
      <SceneController theme={mode} themeIntensity={themeIntensity} />
      <Environment
        preset="studio"
        environmentIntensity={environmentIntensity}
        backgroundIntensity={themeColors.environmentBg}
      />

      {/* Infinite ground plane */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -2, 0]} receiveShadow>
        <planeGeometry args={[100, 100]} />
        <meshLambertMaterial
          color={themeColors.ground}
          transparent
          opacity={0.8}
        />
      </mesh>

      {/* Backdrop wall for better depth perception */}
      <mesh position={[0, 0, -10]} receiveShadow>
        <planeGeometry args={[200, 200]} />
        <meshLambertMaterial
          color={themeColors.background}
          transparent
          opacity={0.3}
        />
      </mesh>
    </>
  );
}
